/**
 * STUB PAGE - Pagina demo per i corsi
 *
 * Questa pagina usa dati mock da corsi.json.
 * Quando il backend sarà pronto, verrà sostituita da una pagina dinamica (get_page).
 *
 * La navigazione a create/edit usa il pageRegistry per mantenere consistenza.
 */

import React, { useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { FiMoreVertical } from "react-icons/fi";
import { MenuItem, Tabs, Tab, Box, Button, Menu as MuiMenu } from "@mui/material";
import { Menu } from "@mui/material";
import {
  RiDeleteBinLine,
  RiDownloadFill,
  RiFilePdf2Line,
  RiMore2Fill,
  RiPrinterLine,
  RiArrowDownSLine,
} from "react-icons/ri";
import { usePermissions } from "@/hooks/usePermissions";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { TERipple } from "tw-elements-react";
import corsiData from "@/assets/data/corsi.json";
import { useStaticList } from "@/hooks/useStaticList";
import { getPageNavigation } from "@/core/config/pageRegistry";

interface Tab {
  id: string;
  label: string;
  count: number;
}

export default function CorsiListStub() {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const [showIcons, setShowIcons] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("FAD");
  const [selectedStatusTab, setSelectedStatusTab] = useState<number>(0);
  const [newButtonAnchorEl, setNewButtonAnchorEl] = useState<null | HTMLElement>(null);
  const { canInsert, canEdit, canDelete } = usePermissions();

  /**
   * Helper per navigare alla pagina corso usando il pageRegistry
   * @param courseData dati del corso (deve contenere `tipo` e `id`)
   * @param mode 'create' | 'edit' | 'view'
   */
  const navigateToCourse = (courseData: any, mode: 'create' | 'edit' | 'view') => {
    const courseType = (courseData?.tipo || 'FAD').toLowerCase();
    const id = courseData?.id;
    const endpoint = mode === 'create'
      ? `form/corsi/${courseType}/new`
      : `form/corsi/${courseType}/${id}`;

    const nav = getPageNavigation(endpoint, courseData);
    if (nav) {
      // Override mode se è view (il registry restituisce sempre edit per id esistente)
      const state = { ...nav.state, mode };
      navigate(nav.route, { state });
    } else {
      console.warn(`No page registered for: ${endpoint}`);
    }
  };

  // Carica stati corso da API
  const { options: statoCorsoOptions } = useStaticList("stato_corso");

  // Create tabs with counts for each course type
  const typeTabs = useMemo(() => {
    const courseTypes = ["FAD", "Aula", "Misto", "Webinar"];
    const tabsData: Tab[] = courseTypes.map((type) => ({
      id: type,
      label: type,
      count: corsiData.filter((corso: any) => corso.tipo === type).length
    }));
    return tabsData;
  }, []);

  // Create status tabs with counts (usa stati da API con fallback)
  const statusTabs = useMemo(() => {
    // Se non ci sono stati dall'API, usa fallback hardcoded
    const apiStatuses = statoCorsoOptions.length > 0
      ? statoCorsoOptions.map(opt => opt.label)
      : ["In Programmazione", "Pubblicato", "Finito"];

    const statuses = ["Tutti", ...apiStatuses];
    const tabsData = statuses.map((status, index) => ({
      label: status,
      value: index,
      count: status === "Tutti"
        ? corsiData.filter((corso: any) => corso.tipo === selectedType).length
        : corsiData.filter((corso: any) => corso.tipo === selectedType && corso.stato === status).length
    }));
    return tabsData;
  }, [selectedType, statoCorsoOptions]);

  // Filter data based on search term, selected course type and status
  const filteredRowData = useMemo(() => {
    let filtered = corsiData;
    
    // Filter by course type
    filtered = filtered.filter((row: any) => row.tipo === selectedType);
    
    // Filter by status
    const selectedStatusLabel = statusTabs[selectedStatusTab]?.label;
    if (selectedStatusLabel && selectedStatusLabel !== "Tutti") {
      filtered = filtered.filter((row: any) => row.stato === selectedStatusLabel);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((row: any) => 
        Object.values(row).some((value) =>
          String(value)?.toLowerCase().includes(searchTerm?.toLowerCase())
        )
      );
    }
    
    return filtered;
  }, [selectedType, selectedStatusTab, searchTerm, statusTabs]);

  // Function to handle type tab change
  const handleTypeTabChange = (typeId: string) => {
    setSelectedType(typeId);
  };

  // Function to handle status tab change
  const handleStatusTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedStatusTab(newValue);
  };

  // Function to handle new button dropdown
  const handleNewButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setNewButtonAnchorEl(event.currentTarget);
  };

  const handleNewButtonClose = () => {
    setNewButtonAnchorEl(null);
  };

  const handleCreateCourse = (courseType: string) => {
    // Usa pageRegistry per navigare alla pagina corretta
    const typeKey = courseType.toLowerCase(); // FAD -> fad, Aula -> aula, etc.
    const endpoint = `form/corsi/${typeKey}/new`;
    const nav = getPageNavigation(endpoint);

    if (nav) {
      navigate(nav.route, { state: nav.state });
    } else {
      console.warn(`No page registered for course type: ${courseType}`);
    }
    handleNewButtonClose();
  };

  function getParams() {
    return {
      skipColumnGroupHeaders: false,
      skipColumnHeaders: false,
      onlySelected: true,
      columnKeys: ["codice", "nome", "categoria", "tipo", "classe", "durataOre", "scadenzaAttivazione", "cfp"],
    };
  }

  const deleteMultipleCorsi = async () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    console.log(selectedRows);
    if (selectedRows !== undefined) {
      // Qui implementeresti la logica di eliminazione
    }
    gridRef.current?.api.applyTransaction({
      remove: selectedRows,
    });
  };

  function onBtnExport() {
    gridRef.current?.api!.exportDataAsCsv(getParams());
  }

  function ActionCellRenderer(params: ICellRendererParams) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };


    const confirmDelete = () => {
      // Qui implementeresti la logica di eliminazione del corso
      console.log("Eliminazione corso:", params.data.id);
      setShowDeleteConfirm(false);
    };

    return (
      <div className="flex flex-row gap-2 justify-center items-center h-full">
        {canEdit("sezione_corsi") && (
          <i
            className="ri-pencil-line text-[23px] text-gray-600 cursor-pointer hover:text-red-700 transition-all duration-500"
            onClick={() => navigateToCourse(params.data, 'edit')}
          />
        )}
        {canDelete("sezione_corsi") && (
          <>
            <span onClick={handleClick}>
              <FiMoreVertical className="text-[23px] text-gray-600 cursor-pointer hover:text-red-700 transition-all duration-500" />
            </span>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigateToCourse(params.data, 'view');
                }}
              >
                Visualizza
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigateToCourse(params.data, 'edit');
                }}
              >
                Modifica
              </MenuItem>
               <MenuItem
                onClick={() => {
                  handleClose();
                }}
              >
                Duplica
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  setShowDeleteConfirm(true);
                }}
              >
                Elimina
              </MenuItem>
            </Menu>
            {/* Delete Confirmation Modal */}
            <Dialog
              open={showDeleteConfirm}
              onClose={() => setShowDeleteConfirm(false)}
            >
              <DialogTitle style={{ fontWeight: "700" }}>
                Conferma eliminazione
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Sei sicuro di voler eliminare questo corso? Questa azione non
                  può essere annullata.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <TERipple rippleColor="light" className="mr-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-6 pb-2.5 pt-3 bg-primary-100 rounded text-sm font-medium border-none ring-0 outline-none uppercase leading-normal text-primary-800 transition duration-150 ease-in-out hover:bg-gray-300 hover:border-none hover:outline-none hover:ring-0 focus:outline-none focus:ring-0"
                  >
                    ANNULLA
                  </button>
                </TERipple>
                <TERipple rippleColor="light" className="mr-3 mb-3">
                  <button
                    type="button"
                    onClick={confirmDelete}
                    className="rounded bg-red-700 px-6 pb-2.5 pt-3 text-sm border-none ring-0 outline-none font-medium uppercase leading-normal text-white transition duration-150 ease-in-out hover:bg-red-700 hover:border-none hover:outline-none hover:ring-0 focus:outline-none focus:ring-0"
                  >
                    CONFERMA
                  </button>
                </TERipple>
              </DialogActions>
            </Dialog>
          </>
        )}
      </div>
    );
  }

  const handleSelectionChanged = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    const anyRowSelected = selectedRows && selectedRows.length > 0;
    setShowIcons(anyRowSelected ?? false);
  };

  // Colonne specifiche per i corsi
  const columnDefs: ColDef[] = [
    {
      headerName: "",
      field: "selection",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      filter: false,
      width: 60,
      maxWidth: 60,
      resizable: false,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "CODICE",
      field: "codice",
      flex: 1,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "NOME",
      field: "nome",
      flex: 2,
      width: 280,
      minWidth:280,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div
            className="font-bold text-[#B80F15] cursor-pointer hover:underline h-full flex items-center justify-center"
            onClick={() => navigateToCourse(params.data, 'view')}
          >
            {params.value}
          </div>
        );
      }
    },
    {
      headerName: "CATEGORIA",
      field: "categoria",
      flex: 1,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "TIPO",
      field: "tipo",
      flex: 1,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "CLASSE",
      field: "classe",
      flex: 0.8,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "DURATA(ORE)",
      field: "durataOre",
      flex: 1,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "SCAD.ATT.",
      field: "scadenzaAttivazione",
      flex: 1.2,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "CFP",
      field: "cfp",
      flex: 0.8,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
    },
    {
      headerName: "",
      field: "actions",
      cellRenderer: ActionCellRenderer,
      minWidth: 100,
      maxWidth: 100,
      cellClass: "ag-cell-center",
      headerClass: "ag-header-center",
      suppressMenu: true,
      sortable: false,
      filter: false,
      pinned: "right",
      resizable: false,
    },
  ];

  const LoadingOrNoDataOverlay = () => {
    if (!corsiData?.length) {
      return (
        <div className="flex justify-center items-center h-full text-gray-500">
          Nessuna riga trovata
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-full gap-0">
      {/* Filters Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-0 pt-5 pb-0 bg-[#F3F3F3]">
          <div className="flex justify-between items-center px-8 box-border">
            {/* Title and counter */}
            <div className="flex gap-2 items-center">
              <h1 className="text-[24px] font-semibold text-gray-900">
                Elenco Corsi
              </h1>
              <span className="ml-2 mt-1 bg-red-200 text-[#B80F15] py-1 px-2.5 rounded-full text-sm font-bold">
                {filteredRowData?.length}
              </span>
              <div
                className={`flex flex-row gap-3 text-black ml-4 mt-1 text-lg ${
                  showIcons ? "block" : "hidden"
                }  transition-all duration-500`}
              >
                <RiDownloadFill
                  onClick={() => onBtnExport()}
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
                <RiFilePdf2Line
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
                <RiPrinterLine
                  onClick={() => {}}
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
                <RiDeleteBinLine
                  onClick={deleteMultipleCorsi}
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
                <RiMore2Fill
                  className={`transition-all duration-200 ease-in-out cursor-pointer hover:text-shblue-500`}
                />
              </div>
            </div>

            {/* Search and New button */}
            <div className="flex gap-3 items-center">
              {(canInsert("sezione_corsi") || true) && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleNewButtonClick}
                    endIcon={<RiArrowDownSLine className="text-[12px]"/>}
                    sx={{
                      backgroundColor: '#B80F15',
                      color: 'white',
                      borderRadius: '4px',
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      padding: '8px 20px',
                      '&:hover': {
                        backgroundColor: '#A00D12',
                      },
                      '&:focus': {
                        outline: 'none',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Nuovo
                  </Button>
                  <MuiMenu
                    anchorEl={newButtonAnchorEl}
                    open={Boolean(newButtonAnchorEl)}
                    onClose={handleNewButtonClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 180,
                        boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        borderRadius: '8px',
                      },
                    }}
                  >
                    <MenuItem onClick={() => handleCreateCourse('FAD')}>
                      Corso FAD
                    </MenuItem>
                    <MenuItem onClick={() => handleCreateCourse('Aula')}>
                      Corso in Aula
                    </MenuItem>
                    <MenuItem onClick={() => handleCreateCourse('Misto')}>
                      Corso Misto
                    </MenuItem>
                    <MenuItem onClick={() => handleCreateCourse('Webinar')}>
                      Webinar
                    </MenuItem>
                  </MuiMenu>
                </>
              )}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Ricerca"
                  className="pl-4 pr-10 py-[9px] border text-sm border-gray-300 rounded-[4px] focus:outline-none focus:border-black focus:ring-black focus:ring-[0.5px] text-gray-500 placeholder-gray-600"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {/* Type Filter buttons */}
          <div className="flex items-center pt-3 pb-2 mt-2 space-x-4  px-8">
            {typeTabs.map((tab: Tab, index: number) => (
              <React.Fragment key={tab.id}>
                <span
                  className={`${
                    selectedType === tab.id
                      ? "text-[#B80F15]"
                      : "text-gray-500 hover:text-gray-700"
                  } text-[20px] font-medium cursor-pointer`}
                  onClick={() => handleTypeTabChange(tab.id)}
                >
                  {tab.label} ({tab.count})
                </span>
                {index < typeTabs.length - 1 && (
                  <div className="h-6 w-[1px] bg-gray-500"></div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Status MUI Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, paddingBottom: 0 }}>
            <Tabs 
              value={selectedStatusTab} 
              onChange={handleStatusTabChange}
              variant="fullWidth"
              sx={{
                outline: 'none',
                
                border: 'none',
                '& .MuiTabs-indicator': {
                  backgroundColor: '#B80F15',
                  height: 3,
                },
                '& .MuiTab-root': {
                  color: '#6B7280',
                  fontWeight: 500,
                  padding: '10px 0px',
                  textTransform: 'uppercase',
                  fontSize: '0.875rem',
                  minHeight: 48,
                  '&.Mui-selected': {
                    color: '#B80F15',
                    fontWeight: 600,
                    outline: 'none',
                    boxShadow: 'none',
                    border: 'none',
                  },
                  '&:hover': {
                    color: '#B80F15',
                    backgroundColor: 'rgba(184, 15, 21, 0.04)',
                  },
                  '&:active':{
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                  },
                  '&:focus':{
                    border: 'none',
                    boxShadow: 'none',
                    outline: 'none',
                  }
                },
              }}
            >
              {statusTabs.map((tab) => (
                <Tab 
                  key={tab.value}
                  label={`${tab.label} (${tab.count})`}
                  value={tab.value}
                />
              ))}
            </Tabs>
          </Box>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-y-hidden flex-1 overflow-x-clip">
        <div className="w-full h-full rounded-none ag-theme-quartz">
          <AgGridReact
            ref={gridRef}
            domLayout="normal"
            autoSizeStrategy={{
              type: "fitGridWidth",
              defaultMinWidth: 100,
            }}
            rowSelection="multiple"
            suppressRowDeselection={true}
            suppressRowClickSelection={true}
            pagination={false}
            onSelectionChanged={handleSelectionChanged}
            paginationAutoPageSize={false}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            pivotPanelShow={"always"}
            rowHeight={65}
            rowData={filteredRowData}
            getRowId={(params) => params.data.id}
            columnDefs={columnDefs}
            readOnlyEdit={true}
            rowGroupPanelShow={"always"}
            overlayNoRowsTemplate=""
            overlayLoadingTemplate=""
            noRowsOverlayComponent={LoadingOrNoDataOverlay}
            loadingOverlayComponent={LoadingOrNoDataOverlay}
          />
        </div>
      </div>
    </div>
  );
}